// historyEntry — "back to where you came from" as ONE pop to the entry that led
// into the surface (ADR-036: the browser history is the only navigation stack;
// the entity page's back chevron and the focused flow's exit are visible proxies
// for it). A surface with inner pushes — an entity's opened files and peeks, a
// link from a peek to another of its tabs — would replay them entry by entry
// under a bare `navigate(-1)`. So the surface stamps each of ITS entries with the
// history index its visit began at (lib/entryState — the entry's own state: a
// refresh, Back and Forward keep it, a link never carries it), and the exit pops
// the exact depth back to the entry before it: the origin's own entry, with its
// FULL URL — the nuqs shallow view state (filters · sort · grouping) that never
// passed through the router included, which a recorded location would lose.
// The surface stays mounted while the person moves inside it, so it stamps every
// new entry itself — no link, tab or overlay carries anything: a push or a
// replace inside the scope takes the start it holds, an entry it has stamped
// before keeps its own (Back into an earlier visit to the same place is that
// visit, never the latest), and any other entry starts a visit there. A deep
// link (the visit's start is the first entry) has no origin, and a memory router
// (a story, a test) is no address: both fall back to the caller's path.

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { currentIndex, liveState, routerIsAddress, visitStartOf, withVisitStart, writeEntry } from './entryState.js';

/**
 * Stamp the visit to `scope` (a stable key for the surface — the entity's route
 * root, the focused route's base) on each of its entries and hand back its exit.
 */
export function useHistoryEntry(scope: string): { exit: (fallback: string) => void } {
  const navigate = useNavigate();
  const navType = useNavigationType();
  const { key } = useLocation();
  // The visit this surface is in — its scope and start, carried on to the entries it moves to.
  const held = useRef<{ scope: string; start: number } | null>(null);
  useEffect(() => {
    if (!routerIsAddress(key)) return;
    const stamped = visitStartOf(liveState(), scope);
    if (stamped !== null) {
      held.current = { scope, start: stamped };
      return;
    }
    const start = held.current?.scope === scope && navType !== 'POP' ? held.current.start : currentIndex();
    writeEntry(withVisitStart(liveState(), scope, start));
    held.current = { scope, start };
  }, [scope, key, navType]);
  const exit = useCallback(
    (fallback: string) => {
      const start = routerIsAddress(key) ? visitStartOf(liveState(), scope) : null;
      const depth = start === null ? 0 : currentIndex() - start + 1;
      // No origin (a deep link, a fresh tab) — or a depth the stack cannot honour: the collection, replacing.
      if (start === null || start <= 0 || depth <= 0 || depth > currentIndex()) navigate(fallback, { replace: true });
      else navigate(-depth);
    },
    [scope, key, navigate],
  );
  return { exit };
}
